/**
 * Performance Monitoring Hook for AllTuc
 * Tracks Core Web Vitals and performance metrics
 */

import { useEffect, useRef } from 'react';
import { logger } from '../infrastructure/logger';

// Type definitions for Performance API extensions
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

interface PerformanceEventTiming extends PerformanceEntry {
  processingStart: number;
  startTime: number;
}

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  value: number;
}

interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
}

export function usePerformanceMonitor() {
  const metricsRef = useRef<PerformanceMetrics>({});

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // Track Largest Contentful Paint (LCP)
    const observerLCP = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry?.startTime) {
        metricsRef.current.lcp = lastEntry.startTime;
      }
    });

    try {
      observerLCP.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      logger.warn('LCP observer not supported');
    }

    // Track First Input Delay (FID)
    const observerFID = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.name === 'first-input') {
          const timingEntry = entry as PerformanceEventTiming;
          if (timingEntry.processingStart && timingEntry.startTime) {
            metricsRef.current.fid = timingEntry.processingStart - timingEntry.startTime;
          }
        }
      });
    });

    try {
      observerFID.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      logger.warn('FID observer not supported');
    }

    // Track Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const observerCLS = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.name === 'layout-shift') {
          const layoutShift = entry as LayoutShiftEntry;
          if (!layoutShift.hadRecentInput && layoutShift.value) {
            clsValue += layoutShift.value;
            metricsRef.current.cls = clsValue;
          }
        }
      });
    });

    try {
      observerCLS.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      logger.warn('CLS observer not supported');
    }

    // Track First Contentful Paint (FCP)
    const observerFCP = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
      if (fcpEntry?.startTime) {
        metricsRef.current.fcp = fcpEntry.startTime;
      }
    });

    try {
      observerFCP.observe({ entryTypes: ['paint'] });
    } catch (e) {
      logger.warn('FCP observer not supported');
    }

    // Monitor memory usage
    const checkMemory = () => {
      const perf = performance as PerformanceWithMemory;
      if (perf.memory) {
        logger.info('Memory usage', {
          used: Math.round(perf.memory.usedJSHeapSize / 1048576) + ' MB',
          total: Math.round(perf.memory.totalJSHeapSize / 1048576) + ' MB',
          limit: Math.round(perf.memory.jsHeapSizeLimit / 1048576) + ' MB'
        });
      }
    };

    // Check memory every 30 seconds
    const memoryInterval = setInterval(checkMemory, 30000);

    // Cleanup
    return () => {
      try {
        observerLCP.disconnect();
      } catch (e) {
        // Observer may already be disconnected
      }
      try {
        observerFID.disconnect();
      } catch (e) {
        // Observer may already be disconnected
      }
      try {
        observerCLS.disconnect();
      } catch (e) {
        // Observer may already be disconnected
      }
      try {
        observerFCP.disconnect();
      } catch (e) {
        // Observer may already be disconnected
      }
      clearInterval(memoryInterval);
    };
  }, []);

  const reportMetrics = () => {
    const metrics = metricsRef.current;

    // Log to console in development
    if (import.meta.env.DEV) {
      logger.info('Performance Metrics', metrics);
    }

    // Send to analytics in production
    if (import.meta.env.PROD && typeof window !== 'undefined' && (window as { gtag?: Function }).gtag) {
      Object.entries(metrics).forEach(([metric, value]) => {
        if (typeof value === 'number' && !isNaN(value)) {
          (window as { gtag?: Function }).gtag?.('event', 'web_vitals', {
            event_category: 'Web Vitals',
            event_label: metric,
            value: Math.round(value),
            non_interaction: true
          });
        }
      });
    }
  };

  return { metrics: metricsRef.current, reportMetrics };
}
